/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Button, Description, Input, Label } from "@nordcloud/gnui";
import { Fieldset } from "~/components";
import { ColorPicker, COLOR_PICKER_COLORS } from "./ColorPicker";

export type FormValues = {
  id: string;
  name: string;
  description: string;
  color: string;
};

type Props = {
  isEditMode?: boolean;
  onSubmit?: SubmitHandler<FormValues>;
  onUpdate?: SubmitHandler<FormValues>;
  initialValues?: FormValues;
  type?: string;
  hasColor?: boolean;
};

export function BusinessContextsForm({
  isEditMode,
  onSubmit = () => undefined,
  onUpdate = () => undefined,
  initialValues,
  type,
  hasColor,
}: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,

    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (hasColor) {
      register("color", { required: true });
      setValue("color", initialValues?.color ?? COLOR_PICKER_COLORS[0]);
    }
    if (initialValues != null) {
      setValue("name", initialValues.name ?? "");
      setValue("description", initialValues.description ?? "");
      setValue("id", initialValues.id ?? "");
    }
  }, [register, setValue, initialValues, hasColor]);

  const watcher = watch();

  return (
    <form
      onSubmit={isEditMode ? handleSubmit(onUpdate) : handleSubmit(onSubmit)}
    >
      {isEditMode && <input type="hidden" {...register("id")} />}
      <Fieldset>
        <Label htmlFor="input-name" name={type ? `${type} Name` : `Name`} />
        <Input
          id="input-name"
          type="text"
          {...register("name", { required: true })}
          placeholder={type ? `${type} Name` : `Name`}
          status={errors.name && "error"}
        />
        {errors.name && <Description>Name is required.</Description>}
      </Fieldset>
      <Fieldset>
        <Label
          htmlFor="input-description"
          name={type ? `${type} Description` : `Description`}
        />
        <Input
          id="input-description"
          type="text"
          {...register("description", { required: true })}
          placeholder={type ? `${type} Description` : `Description`}
          status={errors.description && "error"}
        />
        {errors.description && (
          <Description>Description is required.</Description>
        )}
      </Fieldset>
      {hasColor && (
        <Fieldset>
          <ColorPicker
            value={watcher.color}
            onChange={(value) => setValue("color", value)}
          />
          {errors.color && (
            <Description>Color is required, please select color.</Description>
          )}
        </Fieldset>
      )}
      <Button type="submit">{isEditMode ? "Update" : "Add"}</Button>
    </form>
  );
}
